import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import type { PendingOperation, PendingState } from './offline';
import type { OfflineBatch } from './offlineAdapter';
import type { ClinicalSubject } from './types';
import { displayPayload } from './projection';
const labels: Record<PendingState, string> = {
  pending: 'Awaiting sync',
  'retryable-error': 'Retry scheduled',
  'sign-in-required': 'Sign-in required',
  'receipt-mismatch': 'Receipt needs review',
  forbidden: 'Permission denied',
  conflict: 'Server conflict',
  invalid: 'Entry rejected',
  held: 'Held for review',
};
export function OfflineQueueReview({
  pending,
  subjects,
  onResolve,
  disabled = false,
}: {
  pending: PendingOperation<OfflineBatch>[];
  subjects: ClinicalSubject[];
  onResolve: (id: string, action: 'retry' | 'hold', reason: string) => Promise<void>;
  disabled?: boolean;
}) {
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  async function resolve(id: string, action: 'retry' | 'hold') {
    try {
      await onResolve(id, action, reasons[id] ?? '');
      setError('');
    } catch {
      setError('Review could not be saved. The original batch is retained.');
    }
  }
  return (
    <section className="flex flex-col gap-3" aria-label="Pending synchronization review">
      <h4 className="font-medium">Saved locally — pending server confirmation</h4>
      {pending.map((operation) => {
        const id = operation.envelope.id;
        return (
          <Card key={id}>
            <CardHeader>
              <CardTitle>
                <Badge variant="outline">{labels[operation.state]}</Badge>
              </CardTitle>
              <CardDescription>
                Original entries remain unchanged. Review the server chart before recording a replacement for a
                conflicting or uncertain receipt.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {operation.envelope.payload.inputs.map((input, index) => (
                <p key={index}>
                  {subjects.find((subject) => subject.id === input.subject_id)?.label ?? `Subject ${input.subject_id}`}{' '}
                  · {input.event_type} · {input.occurred_at} · {displayPayload(input.payload)}
                </p>
              ))}
              {operation.state === 'retryable-error' && operation.nextAttemptAt && (
                <p>
                  Next automatic retry after {new Date(operation.nextAttemptAt).toLocaleTimeString()} while this page is
                  open.
                </p>
              )}
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor={`queue-reason-${id}`}>Review reason (required to hold or retry)</FieldLabel>
                  <Textarea
                    id={`queue-reason-${id}`}
                    value={reasons[id] ?? ''}
                    onChange={(event) => setReasons((previous) => ({ ...previous, [id]: event.target.value }))}
                  />
                </Field>
              </FieldGroup>
            </CardContent>
            <CardFooter className="flex flex-wrap gap-3">
              <Button
                className="min-h-11"
                variant="outline"
                disabled={disabled || !reasons[id]?.trim() || operation.state === 'held'}
                onClick={() => void resolve(id, 'hold')}>
                Hold original batch
              </Button>
              <Button
                className="min-h-11"
                disabled={disabled || !reasons[id]?.trim()}
                onClick={() => void resolve(id, 'retry')}>
                Retry unchanged batch
              </Button>
            </CardFooter>
          </Card>
        );
      })}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
